// Skeleton Component
// ==================
// Skeleton loaders that match content shape for better loading UX

import React from 'react';

interface SkeletonProps {
  /** Width of the skeleton (e.g., "100%", "200px", "w-full") */
  width?: string;
  /** Height of the skeleton (e.g., "20px", "h-4") */
  height?: string;
  /** Shape variant */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  /** Additional className */
  className?: string;
  /** Animation type */
  animation?: 'pulse' | 'wave' | 'none';
}

// Base Skeleton element
export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  variant = 'text',
  className = '',
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-gray-200 dark:bg-gray-700';

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%]',
    none: '',
  };

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-lg',
  };

  const style: React.CSSProperties = {};

  // Handle width
  if (width) {
    if (width.startsWith('w-')) {
      // Tailwind class - will be added to className
    } else {
      style.width = width;
    }
  }

  // Handle height
  if (height) {
    if (height.startsWith('h-')) {
      // Tailwind class - will be added to className
    } else {
      style.height = height;
    }
  }

  // Default dimensions
  if (!height && variant === 'text') {
    style.height = '1em';
  }
  if (!width && variant === 'text') {
    style.width = '100%';
  }

  // Build tailwind width/height classes
  const dimensionClasses = [
    width?.startsWith('w-') ? width : '',
    height?.startsWith('h-') ? height : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`${baseClasses} ${animationClasses[animation]} ${variantClasses[variant]} ${dimensionClasses} ${className}`}
      style={Object.keys(style).length > 0 ? style : undefined}
      aria-hidden="true"
    />
  );
};

// Pre-built skeleton patterns for common UI elements

// Card skeleton
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white dark:bg-neutral-dark rounded-xl shadow p-4 ${className}`}>
    <div className="flex items-center gap-3 mb-3">
      <Skeleton variant="circular" width="40px" height="40px" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="60%" height="16px" />
        <Skeleton variant="text" width="40%" height="12px" />
      </div>
    </div>
    <Skeleton variant="rounded" height="60px" className="mb-3" />
    <div className="flex gap-2">
      <Skeleton variant="rounded" width="80px" height="32px" />
      <Skeleton variant="rounded" width="80px" height="32px" />
    </div>
  </div>
);

// Journey/Topic list item skeleton
export const SkeletonListItem: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`flex items-center justify-between p-4 bg-white dark:bg-neutral-dark rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
    <div className="flex items-center gap-3 flex-1">
      <Skeleton variant="rounded" width="48px" height="48px" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="70%" height="18px" />
        <Skeleton variant="text" width="50%" height="14px" />
      </div>
    </div>
    <Skeleton variant="rounded" width="60px" height="24px" />
  </div>
);

// Session list item skeleton
export const SkeletonSessionItem: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`flex items-center justify-between p-3 bg-white dark:bg-neutral-dark rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
    <div className="flex items-center gap-3">
      <Skeleton variant="circular" width="36px" height="36px" />
      <div className="space-y-1.5">
        <Skeleton variant="text" width="120px" height="16px" />
        <Skeleton variant="text" width="80px" height="12px" />
      </div>
    </div>
    <div className="space-y-1.5 text-right">
      <Skeleton variant="text" width="60px" height="12px" />
      <Skeleton variant="text" width="40px" height="12px" />
    </div>
  </div>
);

// Dashboard mode card skeleton
export const SkeletonModeCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`p-4 bg-white dark:bg-neutral-dark rounded-xl border border-gray-200 dark:border-gray-700 ${className}`}>
    <div className="flex items-center gap-3 mb-2">
      <Skeleton variant="circular" width="40px" height="40px" />
      <Skeleton variant="text" width="100px" height="20px" />
    </div>
    <Skeleton variant="text" width="100%" height="14px" className="mb-1" />
    <Skeleton variant="text" width="80%" height="14px" />
  </div>
);

// Dashboard section skeleton
export const SkeletonDashboardSection: React.FC<{
  title?: boolean;
  itemCount?: number;
  className?: string;
}> = ({
  title = true,
  itemCount = 3,
  className = ''
}) => (
  <div className={className}>
    {title && (
      <Skeleton variant="text" width="150px" height="24px" className="mb-4" />
    )}
    <div className="space-y-3">
      {Array.from({ length: itemCount }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  </div>
);

// Full dashboard loading skeleton
export const SkeletonDashboard: React.FC = () => (
  <div className="space-y-6 p-4 md:p-6">
    {/* Welcome section */}
    <div className="space-y-2">
      <Skeleton variant="text" width="250px" height="28px" />
      <Skeleton variant="text" width="200px" height="16px" />
    </div>

    {/* Mode selector */}
    <div>
      <Skeleton variant="text" width="120px" height="20px" className="mb-3" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SkeletonModeCard />
        <SkeletonModeCard />
        <SkeletonModeCard />
      </div>
    </div>

    {/* Two column layout */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <SkeletonDashboardSection title itemCount={2} />
      <SkeletonDashboardSection title itemCount={2} />
    </div>

    {/* Sessions */}
    <div>
      <Skeleton variant="text" width="150px" height="20px" className="mb-3" />
      <div className="space-y-2">
        <SkeletonSessionItem />
        <SkeletonSessionItem />
      </div>
    </div>
  </div>
);

// Topics page loading skeleton
export const SkeletonTopicsPage: React.FC = () => (
  <div className="space-y-4 p-4 md:p-6">
    {/* Header */}
    <div className="flex items-center justify-between mb-4">
      <Skeleton variant="text" width="180px" height="28px" />
      <Skeleton variant="rounded" width="120px" height="36px" />
    </div>

    {/* Filter bar */}
    <div className="flex gap-4 mb-6">
      <Skeleton variant="rounded" width="200px" height="40px" />
      <Skeleton variant="rounded" className="flex-1" height="40px" />
    </div>

    {/* Topic cards */}
    <div className="space-y-4">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  </div>
);

export default Skeleton;
